import React, { useState } from 'react';

import { DetectionIcon } from '@vlabs/icons';
import { FiltersHeader } from '@vlabs/shared/components/filters-panel/FiltersHeader';
import { Group, SideMenu, useWindowSize } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectFacecoverVersion } from '@vlapps/point/src/app/store/selectors';

import { AppliedFiltersBadge, EventTypeVisibilityButton } from './components';
import DetectionAndMatchFiltersPanel from './DetectionAndMatchFiltersPanel';
import { useWorkspaceFilters } from './useWorkspaceFilters';
import { VIDEO_ANALYTICS_FILTERS } from './videoAnalyticsFilters';
import VideoAnalyticsFiltersPanel from './VideoAnalyticsFiltersPanel';
import {
  getDetectionAndMatchFilterActivity,
  getVideoAnalyticsFilterActivity,
} from './workspaceFilterActivity';
import {
  resetWorkspaceEventFilters,
} from './workspaceFiltersModel';
import type { VideoAnalyticType } from './workspaceFiltersModel';
import st from './WorkspaceFiltersPanel.module.sass';

interface WorkspaceFiltersPanelProps {
  className?: string;
  onCloseClick: VoidFunction;
}

type FiltersPanelContent = 'allFilters' | 'detectionAndMatch' | VideoAnalyticType;

function WorkspaceFiltersPanel({
  onCloseClick,
  className = undefined,
}: WorkspaceFiltersPanelProps): React.JSX.Element {
  const { t } = useTranslation();
  const facecoverVersion = useSelector(selectFacecoverVersion);

  const windowSize = useWindowSize();
  const isCompact = windowSize.width < 1920;

  const { filters, setFilters, updateFilters } = useWorkspaceFilters();
  const detectionActivity = getDetectionAndMatchFilterActivity(filters);

  const [currentContent, setCurrentContent] = useState<FiltersPanelContent>('allFilters');
  // 'allFilters' || 'detectionAndMatch || 'crowd' || 'fight' || 'fire' || 'weapon' || 'facecover' || 'lying' || 'handsup'

  const matchedFilter = VIDEO_ANALYTICS_FILTERS.find(
    ({ analyticType }) => analyticType === currentContent,
  );

  const onBackClick = () => {
    setCurrentContent('allFilters');
  };

  return (
    <div className={cn(st.Root, className)}>

      {currentContent === 'allFilters' && (

        <Group
          gapSize="0"
          vertical
        >
          <FiltersHeader onClick={onCloseClick} />

          <SideMenu.MenuItem
            addon={(
              <AppliedFiltersBadge
                count={detectionActivity.totalCount}
                isEnabled={filters.isEnabledDetectionAndMatchEvents}
              />
            )}
            data-testid="workspace.detectionAndMatchFilters"
            leftIcon={<DetectionIcon />}
            onClick={() => setCurrentContent('detectionAndMatch')}
            rightIcon={(
              <EventTypeVisibilityButton
                isEnabled={filters.isEnabledDetectionAndMatchEvents}
                onChange={(isEnabled) => updateFilters({
                  isEnabledDetectionAndMatchEvents: isEnabled,
                })}
                testId="workspace.detectionAndMatchVisibility"
              />
            )}
            value={t('lunapoint:фильтры.обнаружение и совпадение')}
          />
          <div className={st.Divider} />

          {VIDEO_ANALYTICS_FILTERS.map((data) => {
            const { analyticType, titleKey, icon: Icon } = data;
            const analytic = filters.videoAnalytics[analyticType];
            const isEnabled = analytic?.isEnabled === true;
            const activity = getVideoAnalyticsFilterActivity(filters, {
              analyticType,
              facecoverVersion,
              scoreField: data.scoreField,
              scoreFields: data.scoreFields,
            });
            return (
              <React.Fragment key={analyticType}>
                <SideMenu.MenuItem
                  addon={(
                    <AppliedFiltersBadge
                      count={activity.totalCount}
                      isEnabled={isEnabled}
                    />
                  )}
                  data-testid={`videoAnalytics.${analyticType}`}
                  leftIcon={<Icon />}
                  onClick={() => setCurrentContent(analyticType)}
                  rightIcon={(
                    <EventTypeVisibilityButton
                      isEnabled={isEnabled}
                      onChange={(nextIsEnabled) => updateFilters({
                        videoAnalytics: {
                          ...filters.videoAnalytics,
                          [analyticType]: {
                            ...analytic,
                            isEnabled: nextIsEnabled,
                          },
                        },
                      })}
                      testId={`videoAnalytics.${analyticType}.visibility`}
                    />
                  )}
                  value={t(titleKey)}
                />
                <div className={st.Divider} />
              </React.Fragment>
            );
          })}
        </Group>
      )}

      {currentContent === 'detectionAndMatch'
        && (
          <DetectionAndMatchFiltersPanel
            onBackClick={onBackClick}
          />
        )}

      {!!matchedFilter && (
        <VideoAnalyticsFiltersPanel
          data={matchedFilter}
          onBackClick={onBackClick}
        />
      )}

      <div className={st.ButtonPanel}>
        <Button
          className={st.Button}
          data-testid="FilterForm.clearButton"
          kind="negative"
          onClick={() => setFilters(resetWorkspaceEventFilters(filters))}
          size={isCompact ? 'small' : 'medium'}
        >
          {t('button.clear')}
        </Button>
      </div>
    </div>
  );
}

export default WorkspaceFiltersPanel;
